import { ListChecks, Workflow } from 'lucide-react';
import { type ComponentType, lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router';
import type { ModuleDefinition, TaskExtProps } from '../../app/modules';
import { Skeleton } from '../../components/ui/Skeleton';
import { automationTaskAlerts, AutomationTaskBadges } from './TaskBadges';

/*
 * Автоматизація (М5): правила «тригер → умова → дія» з конструктором і пробним запуском, журнал запусків,
 * умови переходів етапів (контрольні питання, формула коментаря), шаблони підзадач і чек-листів, форми
 * з розгалуженням і посиланням для клієнтів, повторювані роботи, пакети налаштувань, таймери строків реакції,
 * запити дій інших модулів. У задачі — розділ «Чек-лист і строки реакції» та значки (прогрес чек-листа,
 * прострочена реакція). Сторінки й розділ картки завантажуються ліниво.
 */

const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const TaskPanelLazy = lazy(() => import('./TaskPanel'));

/** Розділ картки задачі: власна межа Suspense (оболонка панелі модулів її не ставить). */
function AutomationTaskPanel(props: TaskExtProps) {
  return (
    <Suspense
      fallback={
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      }
    >
      <TaskPanelLazy {...props} />
    </Suspense>
  );
}

const automationModule: ModuleDefinition = {
  key: 'automation',
  label: 'Автоматизація',
  icon: Workflow,
  basePath: '/automation',
  scope: 'projects:read',
  order: 70,
  routes: [
    {
      ...page(() => import('./AutomationLayout')),
      children: [
        { index: true, ...page(() => import('./pages/RulesPage')) },
        { path: 'runs', ...page(() => import('./pages/RunsPage')) },
        { path: 'gates', ...page(() => import('./pages/GatesPage')) },
        { path: 'templates', ...page(() => import('./pages/TemplatesPage')) },
        { path: 'forms', ...page(() => import('./pages/FormsPage')) },
        { path: 'recurrences', ...page(() => import('./pages/RecurrencesPage')) },
        { path: 'packages', ...page(() => import('./pages/PackagesPage')) },
        { path: 'sla', ...page(() => import('./pages/SlaPage')) },
        { path: 'requests', ...page(() => import('./pages/RequestsPage')) },
      ],
    },
    // Конструктори — повні сторінки без вкладок модуля.
    { path: 'rules/new', ...page(() => import('./pages/RuleBuilderPage')) },
    { path: 'rules/:ruleId', ...page(() => import('./pages/RuleBuilderPage')) },
    { path: 'forms/:formId', ...page(() => import('./pages/FormBuilderPage')) },
  ],
  task: {
    panel: { title: 'Чек-лист і строки реакції', icon: ListChecks, Component: AutomationTaskPanel },
    Badges: AutomationTaskBadges,
    alerts: automationTaskAlerts,
  },
};

export default automationModule;
